import { Form } from 'antd';
import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { Input } from 'shared/ui/Input';
import { getSelectedWarehouse } from 'widgets/IssueInvoiceModal/model/selectors/getSelectedWarehouse';
import { getSelectedUser } from 'widgets/IssueInvoiceModal/model/selectors/getSelectedUser';
import { TextArea } from 'shared/ui/TextArea';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { getUserName } from 'entities/User';
import { stepperModalActions } from 'features/StepperModal';
import { issueInvoiceModalActions } from 'widgets/IssueInvoiceModal/model/slice/issueInvoiceModalSlice';
import { Button } from 'shared/ui/Button';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { DatePicker } from 'shared/ui/DatePicker';
import { getServerDateFormat } from 'shared/utils/helpers/getDateFormat';
import dayjs from 'dayjs';

export const InvoiceDataStep: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'booking']);

  const selectedWarehouse = useAppSelector(getSelectedWarehouse);
  const selectedUser = useAppSelector(getSelectedUser);
  const dispatch = useAppDispatch();

  const [form] = Form.useForm();

  useEffect(() => {
    if (selectedUser) {
      const fullAddress = `${selectedUser.street} ${selectedUser.house} ${selectedUser.apartment || ''} ${selectedUser.zip} ${
        selectedUser.city
      } ${selectedUser.country} `;

      form.setFieldValue('email', selectedUser.email);
      form.setFieldValue('address', fullAddress);
    }
  }, [form, selectedUser]);

  const invoiceDetailsBullets = useMemo(() => {
    const bullets: TableRow[] = [
      { label: t('Type of Invoice'), value: t('Personal Invoice') },
      { label: t('Warehouse'), value: selectedWarehouse?.name },
      { label: t('Customer'), value: getUserName(selectedUser) },
      { label: t('Email'), value: selectedUser?.email },
    ];

    return bullets;
  }, [t, selectedUser, selectedWarehouse?.name]);

  const goToNextStep = useCallback(
    (values: { email: string; address: string; description: string; dueDate?: string }): void => {
      dispatch(stepperModalActions.changeActiveStepIndex(3));
      dispatch(
        issueInvoiceModalActions.setInvoiceData({
          invoiceEmail: values.email,
          invoiceAddress: values.address,
          description: values.description,
          dueDate: getServerDateFormat(values?.dueDate || dayjs().startOf('day')),
        }),
      );
    },
    [dispatch],
  );

  return (
    <Form className="flex flex-col p-4" name="invoiceData" form={form} layout="vertical" onFinish={goToNextStep}>
      <div className="font-semibold text-2xl mb-4">{t('Invoice data')}</div>

      <div className="flex gap-10">
        <div className="flex-1">
          <Form.Item
            label={t('Email')}
            name="email"
            rules={[
              { type: 'email', message: t('Please enter correct email!') },
              { required: true, message: t('Please enter email!') },
            ]}
          >
            <Input placeholder={t('Please, enter email')} bordered size="small" />
          </Form.Item>

          <Form.Item
            className="flex-1"
            label={t('Address')}
            name="address"
            rules={[{ required: true, message: t('Please enter address!') }]}
          >
            <Input placeholder={t('Please enter address')} bordered size="small" />
          </Form.Item>

          <Form.Item className="w-full" name="dueDate" label={t('Due date')}>
            <DatePicker format={CLIENT_DATE_FORMAT} placeholder={t('Due date')} inputReadOnly className="h-[39px] w-full" />
          </Form.Item>

          <Form.Item name="description" label={t('Description')}>
            <TextArea placeholder={t('Add your description...')} rows={3} bordered />
          </Form.Item>
        </div>
        <div className="flex-1">
          <div className="flex flex-col bg-secondaryLight rounded-lg">
            <BulletsTable heading={t('Preview Invoice Details')} rows={invoiceDetailsBullets} labelClassName="max-w-[220px]" />
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit">{t('Next')}</Button>
      </div>
    </Form>
  );
});
